export function generateTelemetryPrompt(workout, isDistanceStation) {
  let tel = `Date: ${workout.date}\n`;
  tel += `Type: ${workout.type || 'Workout'}\n`;
  if (workout.duration) tel += `Duration: ${workout.duration} min\n`;
  if (workout.calories) tel += `Calories: ${workout.calories} kcal\n`;
  if (workout.distance) tel += `Distance: ${workout.distance} mi\n`;
  if (workout.avgHeartRate) tel += `Avg HR: ${workout.avgHeartRate} bpm\n`;
  if (workout.maxHeartRate) tel += `Max HR: ${workout.maxHeartRate} bpm\n`;
  
  if (workout.sourceData) {
    const s = workout.sourceData;
    if (s.restingHr) tel += `Resting HR: ${s.restingHr} bpm, `;
    if (s.hrv) tel += `HRV: ${s.hrv} ms, `;
    if (s.sleepHours !== null || s.sleepMinutes !== null) {
      tel += `Sleep: ${s.sleepHours || 0}h ${s.sleepMinutes || 0}m, `;
    }
    if (s.sleepScore) tel += `Sleep Score: ${s.sleepScore}/100, `;
    tel += `\n`;
  }
  
  if (workout.stages && workout.stages.length > 0) {
    tel += `Splits / Stages:\n`;
    workout.stages.forEach(st => {
      const dur = st.durationMin > 0 ? `${st.durationMin}m ${st.durationSec}s` : `${st.durationSec}s`;
      const name = st.type === 'Run' ? 'Run' : st.stationName;
      const isDistance = typeof isDistanceStation === 'function' ? isDistanceStation(st.stationName, st.type) : false;
      const distVal = Number(st.distance !== null && st.distance !== undefined ? st.distance : (st.distance_meters || 0));
      const repsVal = Number(st.reps || 0);
      const effectiveReps = repsVal > 0 ? repsVal : (!isDistance && distVal > 0 ? distVal : 0);
      const metricStr = isDistance 
        ? (distVal > 0 ? ` (${distVal}m)` : (st.type === 'Run' ? ' (0m)' : ''))
        : (effectiveReps > 0 ? ` (${effectiveReps} reps)` : '');
      tel += `- Stage ${st.stageNum} (${st.type}): ${name}${metricStr} - Split: ${dur}\n`;
    });
  }
  return tel;
}

export function generateAIComparisonPrompt(tel1, tel2, customInstructions) {
  let instructionsPrompt = '';
  if (customInstructions && customInstructions.trim() !== '') {
    instructionsPrompt = `
    [IMPORTANT: Custom Coaching Rules/Focus to Respect]
    The user has provided the following custom coaching rules, focus areas, and goals that you MUST respect and incorporate into your comparison and suggestions:
    ${customInstructions.trim()}
    `;
  }
  
  return `
    You are "RoxKit AI Coach", a pragmatic, analytical sports performance coach specializing in cardiovascular conditioning, physiological pacing, and athletic endurance.
    
    Compare the following two workouts and provide an objective, grounded, and professional comparative evaluation:
    
    [Workout 1 Telemetry]
    ${tel1}
    
    [Workout 2 Telemetry]
    ${tel2}
    
    ${instructionsPrompt}
    
    [Coaching Guidelines]
    - Terminology: Refer to sessions as "workouts" or "training sessions" (do NOT call them "simulations").
    - Tone & Effort: Grounded, analytical, and practical. Avoid hyperbole or cheesy praise. Accurately reflect whether differences represent actual physiological gains or differing session goals (e.g., easy Zone 2 recovery vs tempo/threshold session).
    
    Please structure your comparative analysis using the following layout:
    1. **Comparative Overview**: Compare duration, workload metrics, and overall physiological load.
    2. **Physiological & Pacing Breakdown**: Contrast heart rate averages/peaks, zones distribution, and speed/pace metrics. State clearly which workout showed better physiological efficiency (e.g., lower average HR at similar/higher intensity, or better pacing splits).
    3. **Key Differences**: Highlight objective differences in execution between Workout 2 and Workout 1.
    4. **Actionable Training Adjustments**: Provide 2-3 specific, practical recommendations on how to adjust subsequent training based on this comparison.
    
    Use bullet points and bold headers for clarity. Keep it concise, analytical, and practical!
  `;
}

export function generateSummaryPrompt(tel, customInstructions) {
  let instructionsPrompt = '';
  if (customInstructions && customInstructions.trim() !== '') {
    instructionsPrompt = `
    [IMPORTANT: Custom Coaching Rules/Focus to Respect]
    The user has provided the following custom coaching rules, focus areas, and goals that you MUST respect and incorporate into your analysis:
    ${customInstructions.trim()}
    `;
  }
  
  return `
    You are "RoxKit AI Coach", a pragmatic, analytical sports performance coach specializing in cardiovascular conditioning, physiological pacing, and athletic endurance.
    
    Review the following workout telemetry and provide an objective, grounded, and professional evaluation:
    
    [Workout Telemetry]
    ${tel}
    
    ${instructionsPrompt}
    
    [Coaching Guidelines]
    - Terminology: Refer to sessions as "workouts" or "training sessions".
    - Tone & Effort: Grounded, analytical, and practical. Avoid hyperbole or cheesy praise. Focus heavily on pacing, heart rate response to workload, and recovery data if provided.
    
    Please structure your analysis strictly using the following 3 sections:
    1. **Executive Summary**: 1 to 2 concise sentences summarizing session volume, workout type, and primary physiological stimulus.
    2. **Key Insights**: Exactly 2 to 3 bullet points evaluating heart rate zones, pacing consistency, and split execution. (CRITICAL: Each bullet must be strictly a single concise sentence).
    3. **Next Focus**: Exactly 1 to 2 actionable recommendations for subsequent training. (CRITICAL: Each bullet must be strictly a single concise sentence).
    
    Use bullet points (- ) and bold headers as specified above.
  `;
}

export function generateDictationPrompt(transcript) {
  return `
You are a voice assistant parser for RoxKit. Your job is to extract workout metrics and splits from a speech transcript and return a structured JSON response.

The user's speech transcript is:
"${transcript}"

Extract and update:
1. "notes": Any feedback, notes, or descriptions of the workout.
2. "appendNotes": true/false (true if the user seems to append or add notes, false if they seem to overwrite or reset notes).
3. "stages": An array of workout splits/stages.
   - If the user is describing or dictating workout splits (e.g. "first split was a 5 minute run, second was 10 squats"), extract and return them in this array.
   - If the user explicitly asks to clear, delete, or remove the splits, return an empty array [].
   - If the user does not mention splits in their speech, return null in this field.

Each split stage in the array must have:
   - "type": "Run", "Strength", or "Rest"
   - "stationName": For "Strength", use exact match from predefined stations if matched: ['SkiErg', 'Sled Push', 'Sled Pull', 'Burpee Broad Jumps', 'Rowing', 'Farmers Carry', 'Sandbag Lunges', 'Wall Balls'].
     If the exercise is NOT in the list, map it to "Other - <Name>" (e.g. "Other - Pushups", "Other - Pullups", "Other - Squats", "Other - Situps").
     For "Run", leave stationName empty or null.
     For "Rest", leave stationName empty or null.
   - "durationMin": minutes (integer). Default is 0.
   - "durationSec": seconds (integer). Default is 0.
   - "reps": reps (integer, optional, only for Strength and only if not distance-based like Rowing/SkiErg).
   - "distance": distance in meters (integer, optional, only for Run or distance-based Strength like Rowing/SkiErg).

Predefined stations requiring distance (in meters) instead of reps: 'Rowing', 'SkiErg'.
Predefined stations requiring reps (repetitions): 'Sled Push', 'Sled Pull', 'Burpee Broad Jumps', 'Farmers Carry', 'Sandbag Lunges', 'Wall Balls', and any custom "Other - <Name>" exercises.

If the transcript contains no info for notes, return null for notes.

Return ONLY the raw JSON object. Do not wrap it in markdown code block formatting or add any comments or extra text.
  `;
}

export function generateRecommendationPrompt(tel, targetMinutes, todayStr, customInstructions, recoveryData) {
  let instructionsPrompt = '';
  if (customInstructions && customInstructions.trim() !== '') {
    instructionsPrompt = `
    [IMPORTANT: Custom Coaching Rules/Focus to Respect]
    The user has provided the following custom coaching rules, focus areas, and goals that you MUST respect and incorporate into your training plan:
    ${customInstructions.trim()}
    `;
  }

  let recoveryBlock = '';
  let recoveryRules = '';
  if (recoveryData) {
    const resting = recoveryData.restingHr ?? recoveryData.restingHR;
    recoveryBlock = `
    [ATHLETE CURRENT READINESS & RECOVERY STATUS]
    - Daily Readiness Score: ${recoveryData.readinessScore !== undefined && recoveryData.readinessScore !== null ? recoveryData.readinessScore + '%' : 'N/A'} (${recoveryData.readinessStatus || 'N/A'})
    - Chronic Training Load (CTL Fitness): ${recoveryData.fitness !== undefined && recoveryData.fitness !== null ? recoveryData.fitness + ' pts' : 'N/A'}
    - Acute Training Load (ATL Fatigue): ${recoveryData.fatigue !== undefined && recoveryData.fatigue !== null ? recoveryData.fatigue + ' pts' : 'N/A'}
    - Training Stress Balance (TSB Form): ${recoveryData.form !== undefined && recoveryData.form !== null ? recoveryData.form + ' pts' : 'N/A'}
    - Resting Heart Rate: ${resting ? resting + ' bpm' : 'N/A'}
    - Heart Rate Variability (HRV): ${recoveryData.hrv ? recoveryData.hrv + ' ms' : 'N/A'}
    - Sleep: ${recoveryData.sleepHours ? recoveryData.sleepHours + ' hrs' : 'N/A'} (Score: ${recoveryData.sleepScore ? recoveryData.sleepScore + '/100' : 'N/A'})
    - Subjective Feeling: Physical ${recoveryData.physicalFeeling || 'N/A'}/10, Mental ${recoveryData.mentalFeeling || 'N/A'}/10
    `;

    recoveryRules = `
    [CRITICAL READINESS & PHYSIOLOGICAL RECOVERY RULES]
    You MUST adhere strictly to the athlete's current readiness and recovery metrics:
    1. If Daily Readiness is < 50% OR Form (TSB) is < -15: The athlete is acutely fatigued or under-recovered. You MUST prescribe strictly active recovery, Zone 2 aerobic base, or mobility work. DO NOT prescribe high-intensity intervals or heavy station efforts. In your Coaching Focus, explicitly explain why you scaled back intensity citing their readiness score, sleep, RHR, or HRV.
    2. If Daily Readiness is >= 75% AND Form (TSB) >= 0: The athlete is in prime condition. You may authorize high-intensity threshold, interval, or HYROX station work.
    3. In the "Coaching Focus" section, you MUST explicitly cite the athlete's current recovery metrics (e.g. readiness score, sleep duration/score, HRV, or resting heart rate) to justify the workout intensity.
    `;
  }
  
  return `
    You are "RoxKit AI Coach", an elite sports performance coach.
    
    Today is ${todayStr}.
    Based on the following recent workout telemetry for this athlete:
    
    ${tel}
    
    ${recoveryBlock}
    
    ${recoveryRules}
    
    ${instructionsPrompt}
    
    The user is asking for a recommended training plan for TODAY.
    They have requested a workout duration of approximately ${targetMinutes} minutes.
    
    Please provide a specific, actionable daily workout plan that fits within ${targetMinutes} minutes.
    Take into account their recent volume, intensity (HR), and recovery metrics (if provided).
    If they trained hard recently or have low recovery, suggest active recovery or Zone 2. If they are rested, suggest intervals or a harder session.
    
    Structure the workout plan to be extremely simple, direct, and itemized like a checklist. Follow this exact format:
    1. **Coaching Focus**: A brief, 1-2 sentence explanation of why you chose this routine based on their gaps and recovery status (e.g., "Your Readiness is 82% with 8.2h sleep and 52 bpm RHR. We are capitalizing on high recovery with threshold Sled Pushes and Z3 intervals today.").
    2. **Exercises Checklist**:
    Use this exact format for each exercise line in the checklist (do not add extra text, notes, or sub-bullets):
    - [Exercise Name] | [Target Duration/Reps/Distance] | [Target Zone or Intensity]
    
    Example:
    - Warm-up Run | 5 mins | Z2 Recovery
    - Sled Push | 4 laps | Max Effort
    - Rowing Cardio | 10 mins | Z2 Aerobic
    - Cool-down Stretch | 5 mins | Z1 Rest
    
    At the very end of your response, you MUST append a simple, direct, and parseable timeline section under the header "### WORKOUT_TIMELINE".
    This section will be parsed by code to guide the athlete using a countdown timer.
    Use this exact format for each step:
    - [Exercise Name] | [Duration in Minutes]m
    
    Example:
    - Dynamic Warm-up | 5m
    - Sled Push Intervals | 10m
    - Rowing Cardio | 15m
    - Cool-down Stretch | 5m
    
    Rules for this timeline section:
    1. Do not add any text, introduction, conclusion, or notes after the "### WORKOUT_TIMELINE" header other than the bullet list.
    2. Ensure each line begins with a hyphen "-" followed by the name, a pipe "|", and the duration in minutes ending with "m".
    3. The sum of all durations must equal exactly the requested ${targetMinutes} minutes.
    4. Keep the exercise names simple and direct (e.g., "Dynamic Warmup", "Rowing Intervals", "Active Stretch"). The "Start Workout" player will follow this sequence.
    
    Keep the tone professional, motivating, and clear. Format the plan using clean markdown (bullets, bolding). DO NOT mention the code, implementation, or HTML.
  `;
}
